const USER = {
	SYSTEM_MANAGEMENT_USERS: 'system_management_users',
	SYSTEM_MANAGEMENT_USERS_CREATE: 'system_management_users_create',
	SYSTEM_MANAGEMENT_USERS_EDIT: 'system_management_users_edit',
	SYSTEM_MANAGEMENT_USERS_DELETE: 'system_management_users_delete',
} as const;

const ROLES_ACTIONS = {
	SYSTEM_MANAGEMENT_ROLE_ACTION: 'system_management_role_action',
	SYSTEM_MANAGEMENT_ROLE_ACTION_CREATE: 'system_management_role_action_create',
} as const;

const ROLES = {
	SYSTEM_MANAGEMENT_ROLES: 'system_management_roles',
	SYSTEM_MANAGEMENT_ROLES_CREATE: 'system_management_roles_create',
} as const;

const NOTIFICATIONS = {
	SYSTEM_MANAGEMENT_NOTIFICATIONS: 'system_management_notifications',
} as const;

const SERVICE_RATES = {
	SYSTEM_MANAGEMENT_SERVICE_RATES: 'system_management_service_rates',
} as const;

const COUPONS = {
	SYSTEM_MANAGEMENT_COUPONS: 'system_management_coupons',
} as const;

// Admin Wallets — read a user's wallet, its credit buckets and its full ledger, and freeze or
// unfreeze it. NOT a borrowed gate: the matching role action is created in-app via Role Actions
// and granted to Super Admin, so this code means exactly what it says. Until it is granted the
// page is invisible to everyone, which is the correct failure direction for a surface that can
// suspend somebody's wallet.
const WALLETS = {
	SYSTEM_MANAGEMENT_WALLETS: 'system_management_wallets',
} as const;

const VOLUNTEERS = {
	SYSTEM_MANAGEMENT_VOLUNTEERS: 'system_management_volunteers',
} as const;

const FEEDBACK = {
	SYSTEM_MANAGEMENT_FEEDBACK: 'system_management_feedback',
} as const;

// Legal — super-admin management of legal document types, versioned legal
// documents (accepted at onboarding), and the consent audit trail. These codes
// must also be added to the backend permission catalogue (Sync Catalogue tool)
// to become grantable to a role.
const LEGAL = {
	SYSTEM_MANAGEMENT_LEGAL_DOCUMENT_TYPES: 'system_management_legal_document_types',
	SYSTEM_MANAGEMENT_LEGAL_DOCUMENT_TYPES_CREATE: 'system_management_legal_document_types_create',
	SYSTEM_MANAGEMENT_LEGAL_DOCUMENT_TYPES_EDIT: 'system_management_legal_document_types_edit',
	SYSTEM_MANAGEMENT_LEGAL_DOCUMENT_TYPES_DELETE: 'system_management_legal_document_types_delete',
	SYSTEM_MANAGEMENT_LEGAL_DOCUMENTS: 'system_management_legal_documents',
	SYSTEM_MANAGEMENT_LEGAL_DOCUMENTS_PUBLISH: 'system_management_legal_documents_publish',
	SYSTEM_MANAGEMENT_LEGAL_CONSENTS: 'system_management_legal_consents',
} as const;

// API Keys — issue keys that let external/partner systems authenticate (a key
// is bound to a Role). These five already exist on the backend and auto-seed to
// admin roles via PermissionBootstrapHostedService; the `system_management`
// domain is already in DOMAIN_LABELS so they surface in the Manage-Actions UI.
// The Sync Permission Catalogue tool (src/right-panel/role-action/SyncCatalogue.tsx)
// reconciles this frontend list against the backend catalogue.
const API_KEYS = {
	SYSTEM_MANAGEMENT_API_KEYS: 'system_management_api_keys',
	SYSTEM_MANAGEMENT_API_KEYS_CREATE: 'system_management_api_keys_create',
	SYSTEM_MANAGEMENT_API_KEYS_VIEW: 'system_management_api_keys_view',
	SYSTEM_MANAGEMENT_API_KEYS_REVOKE: 'system_management_api_keys_revoke',
	SYSTEM_MANAGEMENT_API_KEYS_ROTATE: 'system_management_api_keys_rotate',
} as const;

// Capabilities that are routable but previously had no permission code, so they
// could never be granted to a role or gated in the UI.
const ADMIN_OPERATIONS = {
	SYSTEM_MANAGEMENT_AUDIT_TRAIL: 'system_management_audit_trail',
	SYSTEM_MANAGEMENT_RESULT_NOTIFICATIONS: 'system_management_result_notifications',
	SYSTEM_MANAGEMENT_ELECTION_OFFICIALS: 'system_management_election_officials',
	SYSTEM_MANAGEMENT_REQUEST_LIST: 'system_management_request_list',
	PRESIDING_OFFICERS: 'presiding_officers',
	TOKEN_MANAGEMENT: 'token_management',
} as const;

// Bootstrap permissions that gate the Action-catalogue API itself
// (api.core ActionController `[HasAction(...)]`). Until a role holds these, the
// backend 403s on creating/updating/deleting/assigning actions — so the Sync tool
// must surface them for the catalogue to become self-serviceable.
const ACTION_CATALOGUE = {
	SYSTEM_MANAGEMENT_ACTIONS_CREATE: 'system_management_actions_create',
	SYSTEM_MANAGEMENT_ACTIONS_UPDATE: 'system_management_actions_update',
	SYSTEM_MANAGEMENT_ACTIONS_DELETE: 'system_management_actions_delete',
	SYSTEM_MANAGEMENT_ROLE_ACTIONS_ASSIGN: 'system_management_role_actions_assign',
} as const;

// Channel Governance — super-admin control of which communication channels (SMS,
// Email, WhatsApp, Telegram + social) a party/candidate may use in voter outreach.
// The upsert (PUT /Party/channel-configuration) is Super-Admin-only server-side; this
// code gates the nav entry and page. Add it to the backend permission catalogue via
// the Sync Permission Catalogue tool to make it grantable to a role.
const CHANNEL_GOVERNANCE = {
	SYSTEM_MANAGEMENT_CHANNEL_GOVERNANCE: 'system_management_channel_governance',
} as const;

// Academy Courses — authoring the iElect Academy course catalogue (courses, modules, lessons) on
// api.lms. Not yet in ActionKeys.cs, so the menu item borrows SYSTEM_MANAGEMENT and records this as
// its intendedRoleActions. Add only the variants something reads (JWT header budget).
const LMS = {
	SYSTEM_MANAGEMENT_LMS_COURSES: 'system_management_lms_courses',
} as const;

export const ACTIONS_SYSTEM_MANAGEMENT = {
	SYSTEM_MANAGEMENT: 'system_management',
	...USER,
	...ROLES_ACTIONS,
	...ROLES,
	...NOTIFICATIONS,
	...VOLUNTEERS,
	...SERVICE_RATES,
	...COUPONS,
	...WALLETS,
	...FEEDBACK,
	...API_KEYS,
	...LEGAL,
	...ADMIN_OPERATIONS,
	...ACTION_CATALOGUE,
	...CHANNEL_GOVERNANCE,
	...LMS,
} as const;
